import type { QueryClient } from "@tanstack/react-query";
import { showWarningToast } from "@/utils/toast.utils";
import type { AuditLogMessage } from "./audit-log-message";
import { hasRecentLocalEntityWrite } from "./local-entity-write-registry";
import { mapEntityTypeToQueryKeys } from "./map-entity-type-to-query-keys";
import { incrementLiveUpdateMvtEpoch } from "./mvt-epoch-store";
import { resolveLiveUpdateCloseHref } from "./resolve-live-update-close-href";

const ACTION_PRIORITY = { Create: 1, Update: 2, Delete: 3 } as const;
type LiveAction = keyof typeof ACTION_PRIORITY;

export interface AuditLogGroup {
	action: LiveAction;
	entityType: string;
	entityId: string;
	entityDisplay: string | null;
	fieldNames: Set<string>;
	userIds: Set<string>;
}

export interface ApplyOrgAuditBatchOptions {
	queryClient: QueryClient;
	currentUserId: string | null | undefined;
	pathname: string;
	navigate: (href: string) => void;
	deletedMessage: (entityDisplay: string | null) => string;
}

export function groupAuditLogMessages(
	messages: readonly AuditLogMessage[],
): AuditLogGroup[] {
	const groups = new Map<string, AuditLogGroup>();

	for (const message of messages) {
		if (
			!message.entityType ||
			!message.entityId ||
			!message.action ||
			!(message.action in ACTION_PRIORITY)
		) {
			continue;
		}

		const action = message.action as LiveAction;
		const groupKey = `${message.entityType}:${message.entityId.toLowerCase()}`;
		const existing = groups.get(groupKey);
		if (!existing) {
			groups.set(groupKey, {
				action,
				entityType: message.entityType,
				entityId: message.entityId,
				entityDisplay: message.entityDisplay,
				fieldNames: new Set(message.fieldName ? [message.fieldName] : []),
				userIds: new Set(message.userId ? [message.userId] : []),
			});
			continue;
		}

		if (ACTION_PRIORITY[action] > ACTION_PRIORITY[existing.action]) {
			existing.action = action;
		}
		if (message.entityDisplay) existing.entityDisplay = message.entityDisplay;
		if (message.fieldName) existing.fieldNames.add(message.fieldName);
		if (message.userId) existing.userIds.add(message.userId);
	}

	return [...groups.values()];
}

async function invalidate(
	queryClient: QueryClient,
	keys: readonly (readonly unknown[])[],
): Promise<void> {
	await Promise.all(
		keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
	);
}

export async function applyOrgAuditBatch(
	messages: readonly AuditLogMessage[],
	options: ApplyOrgAuditBatchOptions,
): Promise<void> {
	for (const group of groupAuditLogMessages(messages)) {
		const keys = mapEntityTypeToQueryKeys(group.entityType, group.entityId);
		if (!keys) continue;

		const isThisTab =
			!!options.currentUserId &&
			group.userIds.has(options.currentUserId) &&
			hasRecentLocalEntityWrite(group.entityType, group.entityId);
		if (isThisTab) continue;

		if (keys.isInstance) incrementLiveUpdateMvtEpoch();

		if (group.action === "Create") {
			await invalidate(options.queryClient, [...keys.lists, ...keys.counts]);
			continue;
		}

		if (group.action === "Delete") {
			const closeHref = resolveLiveUpdateCloseHref(
				options.pathname,
				group.entityType,
				group.entityId,
			);
			if (closeHref) {
				showWarningToast({
					message: options.deletedMessage(group.entityDisplay),
				});
				options.navigate(closeHref);
			}
			await invalidate(options.queryClient, [...keys.lists, ...keys.counts]);
			continue;
		}

		await invalidate(options.queryClient, [
			...keys.details,
			...keys.lists,
			...keys.counts,
			...keys.history,
			...keys.validStatuses,
		]);
	}
}
